import { useState } from 'react';
import { Link } from 'react-router';

import { DestinationName } from '@/shared/triage/DestinationName';
import { CATEGORY_LABELS, type EmailCategory } from '@/shared/triage/vocabulary';
import { Badge, type Tone } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Select } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { Table, TBody, Td, Th, THead, Tr } from '@/shared/ui/Table';
import { formatDate } from '@/shared/utils/format';

import { ANSWERS_PAGE } from './api';
import type { FeedbackAnswer, FeedbackFilters } from './types';
import { useFeedbackAnswers } from './useAdmin';

const SIGNALS: Record<string, { label: string; tone: Tone }> = {
  corregido: { label: 'Corregido', tone: 'warn' },
  confirmado: { label: 'Correcto', tone: 'ok' },
  respuesta_deshecha: { label: 'Deshecha', tone: 'neutral' },
  eliminado_sin_revisar: { label: 'Eliminado sin revisar', tone: 'danger' },
  // Contract v10.34: moved or deleted in the mail client without an answer here.
  fuera_sin_revisar: { label: 'Fuera de la bandeja sin revisar', tone: 'warn' },
};

/** Signals with nobody's answer in them: no final category or destination to show. */
const UNANSWERED = new Set(['eliminado_sin_revisar', 'fuera_sin_revisar']);

const whenFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'short' });

const category = (key: string | null | undefined) =>
  key ? (CATEGORY_LABELS[key as EmailCategory] ?? key) : '—';

/**
 * Who answered what, when and with which note (contract v10.33): every «Correcto», every
 * «Corregir a…», every answer undone, every email deleted without one (and since v10.34
 * who deleted it) and every email taken out of the inbox in the mail client without one,
 * the newest first, inside Administración → Correcciones and with its window. The same rows as the
 * review sheet (`triage.csv`), but as they are: it is a superadministrator's screen and
 * does not leave the platform. Each row opens its email.
 */
export function AnswersCard({ filters }: { filters: FeedbackFilters }) {
  const [person, setPerson] = useState('');
  const [signal, setSignal] = useState('');
  const [withNote, setWithNote] = useState(false);
  const [offset, setOffset] = useState(0);
  const answers = useFeedbackAnswers({
    ...filters,
    person: person || undefined,
    signal: signal || undefined,
    with_note: withNote || undefined,
    offset,
  });

  function narrow(apply: () => void) {
    apply();
    setOffset(0);
  }

  const data = answers.data;
  const total = data?.total ?? 0;
  return (
    <Card title="Quién respondió qué">
      <p className="text-ink-3 text-[13px] leading-snug">
        Cada respuesta a una propuesta del sistema, la más reciente primero: quién, cuándo, qué se
        proponía, qué dijo y su nota. Son las mismas filas que la hoja de revisión.
      </p>
      <form
        className="grid items-end gap-3 sm:grid-cols-3"
        aria-label="Filtros de las respuestas"
        onSubmit={(event) => event.preventDefault()}
      >
        <Select
          label="Quién"
          value={person}
          onChange={(event) => narrow(() => setPerson(event.target.value))}
        >
          <option value="">Todos</option>
          {(data?.people ?? []).map((address) => (
            <option key={address} value={address}>
              {address}
            </option>
          ))}
        </Select>
        <Select
          label="Qué"
          value={signal}
          onChange={(event) => narrow(() => setSignal(event.target.value))}
        >
          <option value="">Todo</option>
          {Object.entries(SIGNALS).map(([value, { label }]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <label className="flex h-10 items-center gap-2 text-[14px]">
          <input
            type="checkbox"
            checked={withNote}
            onChange={(event) => narrow(() => setWithNote(event.target.checked))}
          />
          Solo las que llevan nota
        </label>
      </form>

      {answers.isPending ? <Spinner variant="block" label="Cargando las respuestas" /> : null}
      {answers.isError ? (
        <ErrorMessage error={answers.error}>No se pudieron cargar las respuestas</ErrorMessage>
      ) : null}
      {data && data.items.length === 0 ? (
        <EmptyState
          title="Ninguna respuesta con estos filtros"
          hint="Las fechas son las de llegada del correo, como el resto de la pestaña."
        />
      ) : null}
      {data && data.items.length > 0 ? (
        <>
          <Table label="Respuestas" minWidth="56rem">
            <THead>
              <Tr>
                <Th>Cuándo</Th>
                <Th>Quién</Th>
                <Th>Correo</Th>
                <Th>Se proponía</Th>
                <Th>Respondió</Th>
                <Th>Nota</Th>
              </Tr>
            </THead>
            <TBody>
              {data.items.map((row, index) => (
                <AnswerRow key={`${row.email_id}-${row.signal}-${index}`} row={row} />
              ))}
            </TBody>
          </Table>
          <div className="flex items-center justify-between gap-3 text-[13px]">
            <span className="text-ink-3">
              {offset + 1}–{Math.min(offset + ANSWERS_PAGE, total)} de {total}
            </span>
            <span className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - ANSWERS_PAGE))}
              >
                Anteriores
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={offset + ANSWERS_PAGE >= total}
                onClick={() => setOffset(offset + ANSWERS_PAGE)}
              >
                Siguientes
              </Button>
            </span>
          </div>
        </>
      ) : null}
    </Card>
  );
}

function AnswerRow({ row }: { row: FeedbackAnswer }) {
  const signal = SIGNALS[row.signal] ?? { label: row.signal, tone: 'neutral' as Tone };
  const answered = !UNANSWERED.has(row.signal);
  return (
    <Tr>
      <Td className="align-top text-[13px] whitespace-nowrap tabular-nums">
        {row.answered_at ? formatDate(whenFormat, row.answered_at) : '—'}
      </Td>
      <Td className="align-top text-[13px] break-all">
        {row.answered_by ??
          (row.signal === 'fuera_sin_revisar' ? (
            <span className="text-ink-3 break-normal">En su programa de correo</span>
          ) : (
            '—'
          ))}
      </Td>
      <Td className="align-top text-[13px]">
        <Link to={`/correos/${row.email_id}`} className="text-accent block hover:underline">
          {row.subject || '(sin asunto)'}
        </Link>
        <span className="text-ink-3 block break-all">
          {row.sender} · {row.mailbox}
        </span>
      </Td>
      <Td className="align-top text-[13px]">
        {category(row.proposed_category)}
        {row.proposed_destination ? (
          <span className="text-ink-3 block">
            <DestinationName destination={row.proposed_destination} />
          </span>
        ) : null}
      </Td>
      <Td className="align-top text-[13px]">
        <Badge tone={signal.tone}>{signal.label}</Badge>
        {answered && row.final_category ? (
          <span className="mt-1 block">
            <span className={row.category_right === false ? 'font-medium' : undefined}>
              {category(row.final_category)}
            </span>
            {row.final_destination ? (
              <span
                className={
                  row.destination_right === false ? 'block font-medium' : 'text-ink-3 block'
                }
              >
                <DestinationName destination={row.final_destination} />
              </span>
            ) : null}
          </span>
        ) : null}
      </Td>
      <Td className="align-top text-[13px] whitespace-pre-line">{row.note ?? ''}</Td>
    </Tr>
  );
}
